import React from 'react';
import Icon from '../../../components/AppIcon';

const PlatformStatistics = ({ statistics = {} }) => {
  const defaultStats = {
    totalAgents: 0,
    pendingApplications: 0,
    verifiedAgents: 0,
    rejectedApplications: 0,
    recentActivity: [],
    qualityMetrics: {
      averageProcessingTime: '0 days',
      approvalRate: '0%',
      documentCompleteness: '0%'
    }
  };

  const stats = { ...defaultStats, ...statistics };

  const statCards = [
    {
      title: 'Total Agents',
      value: stats?.totalAgents,
      icon: 'Users',
      color: 'bg-primary text-primary-foreground',
      change: '+12 this month'
    },
    {
      title: 'Pending Applications',
      value: stats?.pendingApplications,
      icon: 'Clock',
      color: 'bg-warning text-warning-foreground',
      change: 'Requires attention'
    },
    {
      title: 'Verified Agents',
      value: stats?.verifiedAgents,
      icon: 'Shield',
      color: 'bg-success text-success-foreground',
      change: '+8 this week'
    },
    {
      title: 'Rejected Applications',
      value: stats?.rejectedApplications,
      icon: 'XCircle',
      color: 'bg-destructive text-destructive-foreground',
      change: '3 this month'
    }
  ];

  const qualityMetrics = [
    {
      label: 'Average Processing Time',
      value: stats?.qualityMetrics?.averageProcessingTime,
      icon: 'Timer',
      trend: 'down',
      trendValue: '2 days faster'
    },
    {
      label: 'Approval Rate',
      value: stats?.qualityMetrics?.approvalRate,
      icon: 'TrendingUp',
      trend: 'up',
      trendValue: '+5% this month'
    },
    {
      label: 'Document Completeness',
      value: stats?.qualityMetrics?.documentCompleteness,
      icon: 'FileCheck',
      trend: 'up',
      trendValue: '+12% improvement'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards?.map((card, index) => (
          <div key={index} className="bg-card rounded-lg border border-border shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${card?.color}`}>
                <Icon name={card?.icon} size={24} />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-foreground">{card?.value}</div>
                <div className="text-sm text-muted-foreground">{card?.title}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">{card?.change}</div>
          </div>
        ))}
      </div>
      {/* Quality Metrics */}
      <div className="bg-card rounded-lg border border-border shadow-soft p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quality Metrics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {qualityMetrics?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="p-3 bg-muted rounded-lg">
                  <Icon name={metric?.icon} size={24} className="text-muted-foreground" />
                </div>
              </div>
              <div className="text-xl font-bold text-foreground mb-1">{metric?.value}</div>
              <div className="text-sm text-muted-foreground mb-2">{metric?.label}</div>
              <div className={`text-xs flex items-center justify-center space-x-1 ${
                metric?.trend === 'up' ? 'text-success' : 'text-primary'
              }`}>
                <Icon 
                  name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                  size={12} 
                />
                <span>{metric?.trendValue}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-card rounded-lg border border-border shadow-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
          <button className="text-sm text-primary hover:text-primary/80 font-medium">
            View All
          </button>
        </div>
        
        <div className="space-y-4">
          {stats?.recentActivity?.length > 0 ? (
            stats?.recentActivity?.map((activity, index) => (
              <div key={index} className="flex items-center space-x-4 p-3 hover:bg-muted/50 rounded-lg transition-colors">
                <div className={`p-2 rounded-lg ${
                  activity?.type === 'approval' ? 'bg-success/10 text-success' :
                  activity?.type === 'rejection'? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                }`}>
                  <Icon 
                    name={
                      activity?.type === 'approval' ? 'Check' :
                      activity?.type === 'rejection'? 'X' : 'FileText'
                    } 
                    size={16} 
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{activity?.description}</div>
                  <div className="text-xs text-muted-foreground">{activity?.timestamp}</div>
                </div>
                <div className="text-xs text-muted-foreground">{activity?.reviewer}</div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="Activity" size={32} className="mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">No recent activity</p>
            </div>
          )}
        </div>
      </div>
      {/* Agent Status Breakdown */}
      <div className="bg-card rounded-lg border border-border shadow-soft p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Agent Status Breakdown</h3>
        
        <div className="space-y-4">
          {[
            { status: 'Pending Verification', count: stats?.pendingApplications, color: 'bg-warning', percentage: 25 },
            { status: 'Under Review', count: Math.floor(stats?.totalAgents * 0.15), color: 'bg-primary', percentage: 15 },
            { status: 'Verified', count: stats?.verifiedAgents, color: 'bg-success', percentage: 55 },
            { status: 'Rejected', count: stats?.rejectedApplications, color: 'bg-destructive', percentage: 5 }
          ]?.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${item?.color}`}></div>
                <span className="text-sm text-foreground">{item?.status}</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-32 bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${item?.color}`}
                    style={{ width: `${item?.percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-foreground w-8 text-right">{item?.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformStatistics;